<template>
  <v-container fluid class="pa-4">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-btn
            icon
            class="mr-4"
            @click="goBack"
            color="primary"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-avatar size="56" class="mr-4 primary">
            <v-icon size="32" color="white">mdi-account-check</v-icon>
          </v-avatar>
          <div>
            <h1 class="display-1 mb-1 font-weight-bold">Member Enrollment</h1>
            <p class="subtitle-1 grey--text text--darken-1 mb-0">
              Manage enrollment benefits and specialist selections
            </p>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <!-- Loading State -->
    <v-card v-if="loading" class="mb-4" elevation="2">
      <v-card-text class="pa-6">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
            class="mb-4"
          ></v-progress-circular>
          <div class="title">Loading members...</div>
          <div class="caption grey--text mt-2">Please wait while we fetch your enrollment information</div>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Error State -->
    <v-card v-else-if="error" class="mb-4" elevation="2" outlined>
      <v-alert type="error" prominent border="left" colored-border>
        <v-alert-title class="headline">Error</v-alert-title>
        <div class="mt-2">{{ error }}</div>
      </v-alert>
    </v-card>
    
    <!-- Empty State -->
    <v-card v-else-if="members.length === 0" class="mb-4" elevation="2" outlined>
      <v-card-text class="pa-6 text-center">
        <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-account-off</v-icon>
        <h2 class="headline mb-2">No Members Found</h2>
        <p class="body-1 grey--text">
          No members found for this enrollment. Please contact support if you believe this is an error.
        </p>
      </v-card-text>
    </v-card>
    
    <!-- Members List -->
    <div v-else>
      <v-expansion-panels
        v-model="expandedPanels"
        multiple
        class="member-enrollment-panels"
      >
        <v-expansion-panel
          v-for="(family, index) in members"
          :key="family.subscriberId"
          :value="index"
          class="mb-4"
        >
          <v-expansion-panel-header class="expansion-header">
            <template v-slot:default="{ open }">
              <div class="d-flex align-center" style="width: 100%">
                <v-avatar
                  size="48"
                  :color="open ? 'primary' : 'grey lighten-2'"
                  class="mr-4"
                >
                  <v-icon
                    :color="open ? 'white' : 'grey darken-1'"
                    size="28"
                  >
                    mdi-account-group
                  </v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="font-weight-bold text-h6 mb-1">
                    {{ family.subscriber.FIRST_NAME }} {{ family.subscriber.LAST_NAME }}
                  </div>
                  <div class="d-flex align-center flex-wrap">
                    <v-chip
                      small
                      outlined
                      color="primary"
                      class="mr-2 mb-1"
                    >
                      <v-icon small left>mdi-identifier</v-icon>
                      {{ family.subscriberId }}
                    </v-chip>
                    <span
                      v-if="family.dependents.length > 0"
                      class="caption grey--text"
                    >
                      <v-icon small class="mr-1">mdi-account-child</v-icon>
                      {{ family.dependents.length }} dependent{{ family.dependents.length > 1 ? 's' : '' }}
                    </span>
                  </div>
                </div>
                <v-chip
                  v-if="open"
                  small
                  color="primary"
                  text-color="white"
                  class="ml-auto"
                >
                  <v-icon small left>mdi-chevron-up</v-icon>
                  Expanded
                </v-chip>
              </div>
            </template>
          </v-expansion-panel-header>
          
          <v-expansion-panel-content>
            <div class="pa-4">
              <!-- Subscriber Enrollment Card -->
              <v-card
                class="mb-4 subscriber-card"
                elevation="3"
                outlined
              >
                <v-card-title class="primary white--text">
                  <v-icon class="mr-2" color="white">mdi-account-star</v-icon>
                  <div class="flex-grow-1">
                    <div class="text-h6 font-weight-bold">
                      {{ family.subscriber.FIRST_NAME }} {{ family.subscriber.LAST_NAME }}
                    </div>
                    <div class="caption text--lighten-2 mt-1">
                      Subscriber
                    </div>
                  </div>
                </v-card-title>
                <v-card-text class="pa-4">
                  <member-enrollment-details
                    ref="enrollmentDetails"
                    :key="`subscriber-${family.subscriber.MEMBER_ID}-${reloadKey}`"
                    :member="simplifyMember(family.subscriber)"
                    :insurance="insurance"
                    :enrollment-id="enrollmentId"
                    @select-specialist="openSpecialistModal"
                    @change-specialist="openSpecialistModal"
                    @select-pcp="openPCPModal"
                    @change-pcp="openPCPModal"
                  />
                </v-card-text>
              </v-card>
              
              <!-- Dependents Enrollment Cards -->
              <v-card
                v-for="dependent in family.dependents"
                :key="dependent.MEMBER_ID"
                class="mb-4 dependent-card"
                elevation="3"
                outlined
              >
                <v-card-title class="secondary white--text">
                  <v-icon class="mr-2" color="white">mdi-account-child</v-icon>
                  <div class="flex-grow-1">
                    <div class="text-h6 font-weight-bold">
                      {{ dependent.FIRST_NAME }} {{ dependent.LAST_NAME }}
                    </div>
                    <div class="d-flex align-center mt-1">
                      <span class="caption text--lighten-2 mr-3">
                        Dependent
                      </span>
                      <v-chip 
                        v-if="dependent.RELATIONSHIP"
                        x-small 
                        outlined 
                        color="white"
                        class="text--lighten-3"
                      >
                        {{ dependent.RELATIONSHIP }}
                      </v-chip>
                    </div>
                  </div>
                </v-card-title>
                <v-card-text class="pa-4">
                  <member-enrollment-details
                    ref="enrollmentDetails"
                    :key="`dependent-${dependent.MEMBER_ID}-${reloadKey}`"
                    :member="simplifyMember(dependent)"
                    :insurance="insurance"
                    :enrollment-id="enrollmentId"
                    @select-specialist="openSpecialistModal"
                    @change-specialist="openSpecialistModal"
                    @select-pcp="openPCPModal"
                    @change-pcp="openPCPModal"
                  />
                </v-card-text>
              </v-card>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    
    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import MemberEnrollmentDetails from '../components/enrollment/MemberEnrollmentDetails.vue';

export default {
  name: 'MemberEnrollment',
  components: {
    MemberEnrollmentDetails
  },
  data() {
    return {
      members: [],
      loading: true,
      error: null,
      insurance: 'FamilyHealthChoice', // Default for FHC
      enrollmentId: null,
      expandedPanels: [],
      reloadKey: 0,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 3000
      }
    };
  },
  async mounted() {
    // Get enrollmentId from route params
    this.enrollmentId = this.$route.params.enrollmentId;
    
    if (!this.enrollmentId) {
      this.error = 'No enrollment ID provided. Please select an enrollment first.';
      this.loading = false;
      return;
    }
    
    await this.loadMembers();
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'select-enrollment' });
    },
    async loadMembers() {
      this.loading = true;
      this.error = null;
      try {
        // Add cache busting timestamp to ensure fresh data
        const timestamp = Date.now();
        const url = `/api/enrollment/enrollment/${this.enrollmentId}/members?t=${timestamp}`;
        const response = await axios.get(url);
        
        // Simplify members data
        const rawMembers = response.data || [];
        this.members = rawMembers.map(family => {
          try {
            // Simplify subscriber
            const subscriber = this.simplifyMember(family.subscriber || {});
            
            // Simplify dependents
            const dependents = Array.isArray(family.dependents) 
              ? family.dependents.map(dep => this.simplifyMember(dep)).filter(Boolean)
              : [];
            
            // Return simplified family object
            const subscriberId = family.subscriberId || 
                                (family.subscriber && family.subscriber.MEMBER_ID) || 
                                null;
            const simpleFamily = {
              subscriber: subscriber,
              dependents: dependents,
              subscriberId: subscriberId
            };
            
            // Freeze to prevent reactivity
            return Object.freeze(simpleFamily);
          } catch (e) {
            // Fallback: return minimal frozen object
            return Object.freeze({
              subscriber: family.subscriber ? this.simplifyMember(family.subscriber) : null,
              dependents: [],
              subscriberId: null
            });
          }
        });
        
        // Expand first panel by default
        if (this.members.length > 0) {
          this.expandedPanels = [0];
        } else {
          this.error = 'No members found for this enrollment.';
        }
      } catch (error) {
        if (error.response) {
          const errorMessage = (error.response.data && error.response.data.error) 
            ? error.response.data.error 
            : (error.response.statusText || 'Unknown error');
          this.error = `Error loading members: ${errorMessage}`;
        } else {
          this.error = 'Error loading members. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    // Simplify member object
    simplifyMember(member) {
      if (!member) return null;
      
      try {
        // Extract only essential fields to create a simple, non-reactive object
        const simple = {
          MEMBER_ID: member.MEMBER_ID || member.memberId || member.member_id || null,
          SUBSCRIBER_ID: member.SUBSCRIBER_ID || member.subscriberId || member.subscriber_id || null,
          FIRST_NAME: member.FIRST_NAME || member.firstName || member.first_name || null,
          LAST_NAME: member.LAST_NAME || member.lastName || member.last_name || null,
          DOB: member.DOB || member.dob || member.dateOfBirth || null,
          GENDER: member.GENDER || member.gender || null,
          RELATIONSHIP: member.RELATIONSHIP || member.RELATIONSHIP_TO_SUBSCRIBER || member.relationship || null
        };
        
        // Freeze to prevent Vue from making it reactive
        return Object.freeze(simple);
      } catch (e) {
        // Fallback: return minimal frozen object
        return Object.freeze({
          MEMBER_ID: member.MEMBER_ID || null
        });
      }
    }
  }
};
</script>

<style scoped>
.member-enrollment-panels {
  background: transparent;
}

.member-enrollment-panels .v-expansion-panel {
  background: white;
  margin-bottom: 16px;
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease;
}

.member-enrollment-panels .v-expansion-panel:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.member-enrollment-panels .v-expansion-panel--active {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
}

.expansion-header {
  padding: 20px 24px !important;
}

.subscriber-card {
  border-left: 4px solid #1976d2;
  transition: all 0.3s ease;
}

.subscriber-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(25, 118, 210, 0.2) !important;
}

.dependent-card {
  border-left: 4px solid #424242;
  transition: all 0.3s ease;
}

.dependent-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(66, 66, 66, 0.2) !important;
}

.v-card-title {
  padding: 16px 20px !important;
}

/* Custom scrollbar for panels */
.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>

